export type FormulaNode =
  | { kind: 'number'; text: string }
  | { kind: 'group' | 'negative' | 'percent'; child: FormulaNode }
  | { kind: 'binary'; op: '+' | '-' | '*' | '/' | '^'; left: FormulaNode; right: FormulaNode };

/** Display parser only. No evaluation, HTML injection, or rewriting of quiz answers. */
export const parseFormula = (source: string): FormulaNode | null => {
  const normalized = source.replace(/×/g, '*').replace(/÷/g, '/').replace(/−/g, '-');
  const tokens = normalized.match(/\d+(?:\.\d+)?|[()+*/^%-]/g) || [];
  if (tokens.join('') !== normalized.replace(/\s/g, '') || !tokens.length) return null;
  let cursor = 0;
  const atom = (): FormulaNode => {
    const token = tokens[cursor++]; let node: FormulaNode;
    if (token === '-') node = { kind: 'negative', child: atom() };
    else if (token === '(') {
      node = { kind: 'group', child: sum() };
      if (tokens[cursor++] !== ')') throw new Error('Unclosed group');
    } else if (token && /^\d/.test(token)) node = { kind: 'number', text: token };
    else throw new Error('Invalid atom');
    if (tokens[cursor] === '%') { cursor++; node = { kind: 'percent', child: node }; }
    return node;
  };
  const power = (): FormulaNode => {
    const left = atom();
    if (tokens[cursor] === '^') { cursor++; return { kind: 'binary', op: '^', left, right: power() }; }
    return left;
  };
  const product = (): FormulaNode => {
    let left = power();
    while (tokens[cursor] === '*' || tokens[cursor] === '/') {
      const op = tokens[cursor++] as '*' | '/'; left = { kind: 'binary', op, left, right: power() };
    }
    return left;
  };
  const sum = (): FormulaNode => {
    let left = product();
    while (tokens[cursor] === '+' || tokens[cursor] === '-') {
      const op = tokens[cursor++] as '+' | '-'; left = { kind: 'binary', op, left, right: product() };
    }
    return left;
  };
  try { const node = sum(); return cursor === tokens.length ? node : null; } catch { return null; }
};

export const fractionFactors = (node: FormulaNode): { numerator: FormulaNode[]; denominator: FormulaNode[] } => {
  const numerator: FormulaNode[] = [], denominator: FormulaNode[] = [];
  const collect = (part: FormulaNode, inverse = false) => {
    if (part.kind === 'binary' && (part.op === '*' || part.op === '/')) {
      collect(part.left, inverse); collect(part.right, part.op === '/' ? !inverse : inverse);
    } else (inverse ? denominator : numerator).push(part);
  };
  collect(node); return { numerator, denominator };
};
